import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { UserDTO, UserRole } from '@command620/shared';
import { api, setUnauthorizedHandler } from './api';

type Status = 'loading' | 'authenticated' | 'unauthenticated';

/** Admin > Manager > Rep > Viewer (contract §0). */
const RANK: Record<UserRole, number> = { admin: 3, manager: 2, rep: 1, viewer: 0 };

interface AuthValue {
  user: UserDTO | null;
  status: Status;
  role: UserRole | null;
  /** True when the signed-in role is at least `min` - use to HIDE what a role cannot use (server still enforces). */
  can: (min: UserRole) => boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserDTO | null>(null);
  const [status, setStatus] = useState<Status>('loading');

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      setStatus('unauthenticated');
    });
    const ac = new AbortController();
    api<{ user: UserDTO }>('/auth/me', { signal: ac.signal, skipAuthRedirect: true })
      .then((r) => {
        setUser(r.user);
        setStatus('authenticated');
      })
      .catch((e) => {
        if (e instanceof DOMException && e.name === 'AbortError') return;
        setStatus('unauthenticated');
      });
    return () => {
      ac.abort();
      setUnauthorizedHandler(null);
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const r = await api<{ user: UserDTO }>('/auth/login', { method: 'POST', body: { email, password }, skipAuthRedirect: true });
    setUser(r.user);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST', skipAuthRedirect: true });
    } finally {
      setUser(null);
      setStatus('unauthenticated');
    }
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      status,
      role: user?.role ?? null,
      can: (min) => (user ? RANK[user.role] >= RANK[min] : false),
      login,
      logout,
    }),
    [user, status, login, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
